/* Winter Haus — interactions for the brand sections. No dependencies. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const store = {
    get(k) { try { return localStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { localStorage.setItem(k, v); } catch (e) { /* storage blocked */ } }
  };
  const pad = (n) => String(n).padStart(2, '0');

  /* ---------- Countdown: top bar clock, banner tiles, inline product block ---------- */
  if (!customElements.get('wh-countdown')) {
    customElements.define('wh-countdown', class extends HTMLElement {
      connectedCallback() {
        this.cutoff = new Date(this.dataset.cutoff);
        if (isNaN(this.cutoff)) return;
        this.tick();
        const every = this.dataset.mode === 'clock' ? 1000 : 30000;
        this.timer = setInterval(() => this.tick(), every);
      }
      disconnectedCallback() { clearInterval(this.timer); }
      tick() {
        const ms = this.cutoff - new Date();
        if (ms <= 0) return this.over();
        const d = Math.floor(ms / 864e5);
        const h = Math.floor((ms % 864e5) / 36e5);
        const m = Math.floor((ms % 36e5) / 6e4);
        const s = Math.floor((ms % 6e4) / 1e3);
        const mode = this.dataset.mode;
        if (mode === 'clock' || mode === 'tiles') {
          const set = (u, v) => { const el = this.querySelector(`[data-unit="${u}"]`); if (el) el.textContent = pad(v); };
          set('d', d); set('h', h); set('m', m); set('s', s);
        } else {
          const live = this.querySelector('[data-live]');
          if (!live) return;
          const left = d > 0 ? `${d} day${d === 1 ? '' : 's'} ${h}h` : `${h}h ${m}m`;
          live.innerHTML = `Order in the next <strong>${left}</strong> for delivery before Christmas`;
        }
      }
      over() {
        clearInterval(this.timer);
        const msg = this.dataset.after || '';
        if (this.dataset.mode === 'tiles') {
          this.classList.add('is-over');
          const after = this.querySelector('[data-after-text]');
          if (after && msg) { after.textContent = msg; after.hidden = false; } else { this.hidden = true; }
        } else if (this.dataset.mode === 'inline') {
          const live = this.querySelector('[data-live]');
          if (live && msg) live.textContent = msg; else this.hidden = true;
        } else {
          this.hidden = true;
        }
      }
    });
  }

  /* ---------- Product highlight lights switch ---------- */
  if (!customElements.get('wh-lights')) {
    customElements.define('wh-lights', class extends HTMLElement {
      connectedCallback() {
        const btn = this.querySelector('[data-lights-toggle]');
        if (!btn) return;
        const label = btn.querySelector('.wh-switch__label');
        btn.addEventListener('click', () => {
          const on = this.dataset.state !== 'on';
          this.dataset.state = on ? 'on' : 'off';
          btn.setAttribute('aria-pressed', String(on));
          if (label) label.textContent = on ? label.dataset.on : label.dataset.off;
        });
      }
    });
  }

  /* ---------- Scratch-the-frost popup ---------- */
  if (!customElements.get('wh-frost-popup')) {
    customElements.define('wh-frost-popup', class extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        if (!this.dialog) return;
        this.querySelectorAll('[data-close]').forEach((b) => b.addEventListener('click', () => this.close()));
        this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.close(); });
        this.dialog.addEventListener('cancel', () => store.set('wh-frost-done', 'dismissed'));
        this.querySelector('[data-reveal]')?.addEventListener('click', () => this.reveal());
        this.querySelector('[data-copy]')?.addEventListener('click', (e) => {
          const code = this.querySelector('[data-code]')?.textContent.trim();
          const btn = e.currentTarget;
          navigator.clipboard?.writeText(code).then(() => { btn.textContent = 'Copied'; }).catch(() => {});
        });
        const urgent = this.querySelector('[data-open-now]');
        if (urgent) {
          if (this.querySelector('[data-joined]')) store.set('wh-frost-done', 'joined');
          else this.reveal(true);
          return this.open();
        }
        if (store.get('wh-frost-done') || window.Shopify?.designMode) return;
        const delay = Math.max(3, parseInt(this.dataset.delay, 10) || 8) * 1000;
        this.timer = setTimeout(() => this.open(), delay);
        this.onLeave = (e) => { if (e.clientY <= 0 && !e.relatedTarget) this.open(); };
        document.addEventListener('mouseout', this.onLeave);
      }
      disconnectedCallback() { clearTimeout(this.timer); document.removeEventListener('mouseout', this.onLeave); }
      open() {
        clearTimeout(this.timer);
        document.removeEventListener('mouseout', this.onLeave);
        if (this.dialog.open || document.querySelector('dialog[open]:not(.wh-frost__dialog)')) return;
        try { this.dialog.showModal(); } catch (e) { this.dialog.setAttribute('open', ''); }
        requestAnimationFrame(() => this.paintFrost());
      }
      close() {
        if (!store.get('wh-frost-done')) store.set('wh-frost-done', 'dismissed');
        this.dialog.close();
      }
      paintFrost() {
        const canvas = this.querySelector('[data-canvas]');
        if (!canvas || canvas.dataset.ready || this.revealed) return;
        const box = canvas.getBoundingClientRect();
        if (!box.width) return;
        canvas.dataset.ready = '1';
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = box.width * dpr; canvas.height = box.height * dpr;
        const ctx = canvas.getContext('2d');
        ctx.scale(dpr, dpr);
        const w = box.width, h = box.height;
        const g = ctx.createLinearGradient(0, 0, w, h);
        g.addColorStop(0, '#f4f9fd'); g.addColorStop(0.5, '#dcecf8'); g.addColorStop(1, '#eef6fc');
        ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
        for (let i = 0; i < 900; i++) { // frost grain
          ctx.fillStyle = `rgba(255,255,255,${Math.random() * 0.7})`;
          ctx.fillRect(Math.random() * w, Math.random() * h, 1.4, 1.4);
        }
        ctx.strokeStyle = 'rgba(255,255,255,0.9)'; ctx.lineWidth = 1; // ice crystals
        for (let i = 0; i < 26; i++) {
          const x = Math.random() * w, y = Math.random() * h, r = 6 + Math.random() * 16;
          for (let k = 0; k < 6; k++) {
            const a = (Math.PI / 3) * k;
            ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + Math.cos(a) * r, y + Math.sin(a) * r); ctx.stroke();
          }
        }
        ctx.fillStyle = '#2350a8'; ctx.font = '600 15px Figtree, Arial, sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Rub the frost away  ❄', w / 2, h / 2 + 5);
        ctx.globalCompositeOperation = 'destination-out';
        let down = false, last = null;
        const pos = (e) => { const r = canvas.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
        const scratch = (p) => {
          ctx.lineWidth = 38; ctx.lineCap = 'round';
          ctx.beginPath(); ctx.moveTo((last || p).x, (last || p).y); ctx.lineTo(p.x, p.y); ctx.stroke();
          last = p;
        };
        canvas.addEventListener('pointerdown', (e) => { down = true; last = null; canvas.setPointerCapture(e.pointerId); scratch(pos(e)); });
        canvas.addEventListener('pointermove', (e) => { if (down) scratch(pos(e)); });
        const up = () => { if (!down) return; down = false; last = null; if (this.cleared(ctx, canvas) > 0.45) this.reveal(); };
        canvas.addEventListener('pointerup', up);
        canvas.addEventListener('pointercancel', up);
      }
      cleared(ctx, canvas) {
        const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
        let clear = 0, total = 0;
        for (let i = 3; i < data.length; i += 64) { total++; if (data[i] === 0) clear++; }
        return clear / total;
      }
      reveal(silent) {
        if (this.revealed) return;
        this.revealed = true;
        this.classList.add('is-revealed');
        this.querySelector('[data-reveal]')?.setAttribute('hidden', '');
        const fields = this.querySelector('[data-fields]');
        if (fields) { fields.hidden = false; if (!silent) setTimeout(() => fields.querySelector('input[type="email"]')?.focus(), 450); }
      }
    });
  }


  /* ---------- Before / after slider ---------- */
  if (!customElements.get('wh-compare')) {
    customElements.define('wh-compare', class extends HTMLElement {
      connectedCallback() {
        const range = this.querySelector('input[type="range"]');
        if (!range) return;
        const set = (v) => this.style.setProperty('--pos', `${v}%`);
        range.addEventListener('input', () => set(range.value));
        set(range.value);
      }
    });
  }

  /* ---------- Buttons that scroll back up to the buy box ---------- */
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-wh-scroll-buy]');
    if (!link) return;
    const target = document.querySelector('product-form-component, form[action*="/cart/add"], .product-details');
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    // Shortcut photos in the closing section also pick that option in the buy box
    const name = link.dataset.whPickOption;
    const value = link.dataset.whPickValue;
    if (name && value) {
      const input = [...document.querySelectorAll('variant-picker input[type="radio"]')]
        .find((i) => i.dataset.optionName === name && i.value === value);
      if (input && !input.checked) setTimeout(() => input.click(), reduceMotion ? 0 : 450);
    }
  });

  /* ---------- Sideways card rows with arrows + progress line ---------- */
  if (!customElements.get('wh-slider')) {
    customElements.define('wh-slider', class extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        this.bar = this.querySelector('[data-progress]');
        if (!this.track) return;
        this.arrows = [...this.querySelectorAll('[data-dir]')];
        this.arrows.forEach((b) => b.addEventListener('click', () => {
          const card = this.track.firstElementChild;
          const step = card ? card.getBoundingClientRect().width + 16 : this.track.clientWidth * 0.8;
          this.track.scrollBy({ left: step * Number(b.dataset.dir), behavior: reduceMotion ? 'auto' : 'smooth' });
        }));
        this.update = () => {
          const max = this.track.scrollWidth - this.track.clientWidth;
          const x = this.track.scrollLeft;
          this.classList.toggle('is-static', max <= 4);
          if (this.arrows[0]) this.arrows[0].disabled = x <= 4;
          if (this.arrows[1]) this.arrows[1].disabled = x >= max - 4;
          if (this.bar) {
            const vis = this.track.clientWidth / this.track.scrollWidth;
            this.bar.style.width = `${Math.max(vis, 0.12) * 100}%`;
            this.bar.style.transform = `translateX(${max > 0 ? (x / max) * ((1 / Math.max(vis, 0.12)) - 1) * 100 : 0}%)`;
          }
        };
        this.track.addEventListener('scroll', this.update, { passive: true });
        window.addEventListener('resize', this.update);
        this.update();
      }
      disconnectedCallback() { window.removeEventListener('resize', this.update); }
    });
  }

  /* ---------- Bundle & save: tiers, per-item model choices, add the whole bundle ---------- */
  if (!customElements.get('wh-bundles')) {
    customElements.define('wh-bundles', class extends HTMLElement {
      connectedCallback() {
        const data = this.querySelector('[data-variants]');
        this.data = data ? JSON.parse(data.textContent) : null;
        this.picks = this.querySelector('[data-picks]');
        this.qty = 1;
        this.off = 0;
        this.addEventListener('change', (e) => {
          const r = e.target.closest('input[type="radio"]');
          if (r) this.setTier(r);
        });
        this.addEventListener('click', (e) => {
          const chip = e.target.closest('.wh-bpick__chip');
          if (chip) {
            chip.closest('.wh-bpick__opt').querySelectorAll('.wh-bpick__chip').forEach((c) => c.setAttribute('aria-pressed', String(c === chip)));
            this.refresh();
          }
          if (e.target.closest('[data-add]')) this.addBundle();
        });
        const first = this.querySelector('input[type="radio"]:checked');
        if (first) this.setTier(first);
      }
      setTier(r) {
        this.querySelectorAll('.wh-bundles__tier').forEach((t) => t.classList.toggle('is-active', t.contains(r)));
        this.qty = Number(r.value) || 1;
        this.off = Number(r.closest('.wh-bundles__tier')?.dataset.off || 0);
        const multi = this.qty > 1 && this.picks && this.data;
        const box = this.closest('.product-details') || this.parentElement;
        box?.classList.toggle('wh-bundle-multi', !!multi);
        if (this.picks) {
          this.picks.hidden = !multi;
          this.picks.querySelectorAll('[data-item]').forEach((row, i) => { row.hidden = i >= this.qty; });
        }
        if (!multi) {
          const scope = this.closest('.product-details, .shopify-section') || document;
          const q = scope.querySelector('input[name="quantity"]');
          if (q) { q.value = this.qty; q.dispatchEvent(new Event('input', { bubbles: true })); q.dispatchEvent(new Event('change', { bubbles: true })); }
        }
        this.refresh();
      }
      chosen() {
        if (!this.data) return [];
        return [...this.picks.querySelectorAll('[data-item]')].slice(0, this.qty).map((row) => {
          const vals = [...row.querySelectorAll('.wh-bpick__opt')].map((o) => o.querySelector('[aria-pressed="true"]')?.dataset.value);
          return this.data.variants.find((v) => v.options.every((x, i) => x === vals[i]));
        });
      }
      money(cents) {
        try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.data?.currency || 'USD' }).format(cents / 100); }
        catch (e) { return '$' + (cents / 100).toFixed(2); }
      }
      refresh() {
        const label = this.querySelector('[data-add-label]');
        if (!label || this.qty < 2) return;
        const items = this.chosen();
        const ok = items.every((v) => v && v.available);
        const total = items.reduce((s, v) => s + (v ? v.price : 0), 0);
        const disc = Math.round(total * (100 - this.off) / 100);
        label.textContent = ok ? `Add ${this.qty} to cart · ${this.money(disc)}` : 'One of your choices is sold out';
        this.querySelector('[data-add]').disabled = !ok;
      }
      async addBundle() {
        const items = this.chosen();
        if (!items.length || items.some((v) => !v)) return;
        const grouped = {};
        items.forEach((v) => { grouped[v.id] = (grouped[v.id] || 0) + 1; });
        const btn = this.querySelector('[data-add]');
        btn.disabled = true; btn.classList.add('is-loading');
        try {
          const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: Object.entries(grouped).map(([id, quantity]) => ({ id: Number(id), quantity })) }),
          });
          if (!res.ok) throw new Error('add failed');
          window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
        } catch (e) {
          this.querySelector('[data-error]').hidden = false;
          btn.disabled = false; btn.classList.remove('is-loading');
        }
      }
    });
  }

  /* ---------- Videos (reels, reviews): play muted only while visible ---------- */
  const videos = document.querySelectorAll('.wh-reel__video');
  if (videos.length && 'IntersectionObserver' in window && !reduceMotion) {
    const io = new IntersectionObserver((entries) => entries.forEach((en) => {
      const v = en.target;
      if (en.isIntersecting) { v.muted = true; v.play?.().catch(() => {}); } else { v.pause?.(); }
    }), { threshold: 0.5 });
    videos.forEach((v) => io.observe(v));
  }
})();
